/**
 * A {@link TamsClient} that reads a store's flow metadata and segments through
 * Mimir's item-scoped backend endpoint instead of hitting the store directly.
 * The store OAuth token stays in the backend; the consumer (browser cutter,
 * MSE player, or render worker) only ever calls per-item-gated Mimir endpoints.
 *
 * It satisfies the read subset playback and rendering actually use — `getFlow`
 * (flow range/metadata) and `getSegments` (timerange listing). `getFlow` is
 * answered locally from the {@link PlayableArtifactTams} the DTO already
 * resolved (no store round trip); only `getSegments` reaches the backend, via
 * the injected {@link BackendTamsSegmentFetcher}. The admin-browser concerns
 * (`getSources`/`getFlows`/`getSource`) are not part of playback and throw.
 *
 * See `tams-nonuniform-playback-plan.md`.
 */

import type { TamsClient, TamsFlowsFilter, TamsSegmentsParameters } from '../types/tams';
import type { PlayableArtifactTams } from '../dto/playableItemDto';
import type { TamsFlow, TamsSegment, TamsSource } from './schemas';
import type { TamsSegmentListEntry } from '../dto/tams';

/**
 * Fetch the segments overlapping a timerange for one flow, through the backend
 * segment-listing endpoint. The concrete transport (a REST call in the browser,
 * a worker-bridge message in the render worker) is injected so this module has
 * no client/transport dependency.
 */
export type BackendTamsSegmentFetcher =
  (parameters: { flowId: string, timerange: string | undefined, limit: number | undefined, reverse_order: boolean | undefined }) => Promise<TamsSegmentListEntry[]>;

/** The get_urls label the proxied client tags a backend-resolved presigned URL with. */
const PROXIED_PRESIGNED_LABEL = 'presigned';

export function createBackendProxiedTamsClient(
  fetcher: BackendTamsSegmentFetcher,
  artifact: PlayableArtifactTams,
): TamsClient {
  const knownFlowIds = new Set<string>([...artifact.proxyFlowIds, ...artifact.highresFlowIds]);

  return {
    async getSources(): Promise<TamsSource[]> {
      throw new Error('getSources is not available through the backend-proxied TAMS client');
    },
    async getSource(): Promise<TamsSource | null> {
      throw new Error('getSource is not available through the backend-proxied TAMS client');
    },
    async getFlows(_filter: TamsFlowsFilter): Promise<TamsFlow[]> {
      throw new Error('getFlows is not available through the backend-proxied TAMS client');
    },
    async getFlow(flowId: string): Promise<TamsFlow | null> {
      // Answered locally from the resolved artifact — no store round trip. A
      // flow the artifact does not know about is reported as absent, matching
      // the store returning 404 for an unknown flow.
      if (!knownFlowIds.has(flowId)) {
        return null;
      }
      const timerange = artifact.flowTimeranges[flowId];
      const flow: TamsFlow = {
        id: flowId,
        // The consumers that read `getFlow` (findTamsFlowRange) use only
        // `timerange`; source id / format are not consulted on the proxy path.
        source_id: '',
        format: 'urn:x-nmos:format:video',
        ...(timerange !== undefined && { timerange }),
      };
      return flow;
    },
    async getSegments(parameters: TamsSegmentsParameters): Promise<TamsSegment[]> {
      const entries = await fetcher({
        flowId: parameters.flowId,
        timerange: parameters.timerange,
        limit: parameters.limit,
        reverse_order: parameters.reverse_order,
      });
      return entries.map(segmentFromListEntry);
    },
  };
}

/**
 * Wrap a trimmed `{ timerange, url }` entry back into the {@link TamsSegment}
 * shape the timerange-addressed consumers expect. The single backend-resolved
 * presigned URL is exposed as the sole `get_urls` entry so
 * `selectTamsSegmentUrl`/`tamsSegmentUrlOrNull` pick it up unchanged. `object_id`
 * is set to the timerange (the backend does not expose store object ids); the
 * playback/render consumers never address a segment by object id.
 */
function segmentFromListEntry(entry: TamsSegmentListEntry): TamsSegment {
  return {
    object_id: entry.timerange,
    timerange: entry.timerange,
    get_urls: [{ label: PROXIED_PRESIGNED_LABEL, url: entry.url }],
  };
}
